import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Select,
  Input,
  DatePicker,
  Button,
  message,
  Divider,
} from "antd";
import { Api_Url } from "../../../config/config";

const { TextArea } = Input;
const { Option } = Select;

// Mapping based on your provided image
const COLOCATION_MAPPING = {
  Jodhpur: ["Pali", "N/A"],
  Chandigarh: ["Patiala", "N/A"],
  Ludhiana: ["Jalandhar", "N/A"],
  Bathinda: ["Faridkot", "Barnala", "Moga", "Sri Ganganagar", "N/A"],
};

export const BranchIssueModalAdd = ({ onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [fetchingBranches, setFetchingBranches] = useState(false);

  // Watch the BranchId field
  const selectedBranchId = Form.useWatch("BranchId", form);

  // Helper to get selected branch name and options
  const selectedBranchName = branches.find(
    (b) => b.id === selectedBranchId
  )?.name;
  const colocationOptions = COLOCATION_MAPPING[selectedBranchName] || [];

  useEffect(() => {
    const fetchBranches = async () => {
      setFetchingBranches(true);
      try {
        const response = await fetch(`${Api_Url}api/v1/Branch`, {
          headers: {
            Authorization: `Bearer ${
              JSON.parse(sessionStorage.getItem("user")).accessToken
            }`,
          },
        });
        const result = await response.json();
        if (response.ok) {
          setBranches(result.data || []);
        }
      } catch (error) {
        message.error("Failed to load branches");
      } finally {
        setFetchingBranches(false);
      }
    };
    fetchBranches();
  }, []);

  // Update ColocationCenter when branch changes
  useEffect(() => {
    if (colocationOptions.length > 0) {
      // Automatically set to "N/A" as default for consistency
      form.setFieldValue("ColocationCenter", "N/A");
    } else {
      form.setFieldValue("ColocationCenter", undefined);
    }
  }, [selectedBranchName, colocationOptions.length, form]);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      // Determine if the currently selected branch actually has co-location options
      const hasColocationOptions = colocationOptions.length > 0;

      const payload = {
        BranchId: values.BranchId,
        // If the branch has options, send the selected value (which defaults to N/A).
        // If the branch DOES NOT have options, send "N/A" to keep the DB consistent.
        ColocationCenter: hasColocationOptions
          ? values.ColocationCenter || "N/A"
          : "N/A",
        Issue: values.Issue,
        IssueRaisedDate: values.IssueRaisedDate.format("YYYY-MM-DD"),
        Remarks: values.Remarks || "",
      };

      const response = await fetch(`${Api_Url}api/v1/BranchIssue`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${
            JSON.parse(sessionStorage.getItem("user")).accessToken
          }`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        message.success("Branch issue raised successfully");
        onSuccess();
      } else {
        const result = await response.json();
        throw new Error(result.message || "Failed to raise issue");
      }
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <span style={{ fontSize: "18px", fontWeight: "bold" }}>
          Raise New Branch Issue
        </span>
      }
      open={true}
      onCancel={onClose}
      footer={[
        <Button key="back" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={() => form.submit()}
        >
          Submit Issue
        </Button>,
      ]}
      centered
      width={600}
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          name="BranchId"
          label="Select Branch"
          rules={[{ required: true, message: "Please select a branch" }]}
        >
          <Select
            showSearch
            placeholder="Search and select branch"
            optionFilterProp="children"
            loading={fetchingBranches}
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            {branches.map((b) => (
              <Option key={b.id} value={b.id}>
                {b.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* Dynamic Co-location Dropdown - Uniform & Required */}
        {colocationOptions.length > 0 && (
          <Form.Item
            name="ColocationCenter"
            label="Co-location Center"
            rules={[
              { required: true, message: "Please select a center or N/A" },
            ]}
          >
            <Select placeholder="Select center">
              {colocationOptions.map((center) => (
                <Option key={center} value={center}>
                  {center}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item
          name="IssueRaisedDate"
          label="Issue Raised Date"
          rules={[{ required: true, message: "Please select the date" }]}
        >
          <DatePicker
            style={{ width: "100%" }}
            format="DD-MM-YYYY"
            placeholder="Select Date"
          />
        </Form.Item>

        <Divider plain>Issue Details</Divider>

        <Form.Item
          name="Issue"
          label="Description of Issue"
          rules={[
            { required: true, message: "Please describe the issue" },
            {
              min: 10,
              message: "Description should be at least 10 characters",
            },
          ]}
        >
          <TextArea
            rows={4}
            placeholder="Type the branch issue details here..."
          />
        </Form.Item>

        <Form.Item name="Remarks" label="Remarks (Optional)">
          <TextArea rows={2} placeholder="Any additional notes..." />
        </Form.Item>
      </Form>
    </Modal>
  );
};
